import { useState } from "react";
import { createPortal } from "react-dom";
import { apiRequest } from "../../../network/apiClient";
import { API_ENDPOINTS } from "../../../api/apiEndpoints";
import { toast } from "react-toastify";

interface RemoveReferrerButtonProps {
  id: number | string;
  lead_source: string;
  refetch: () => void;
}

const RemoveReferrerButton = ({ id, lead_source, refetch }: RemoveReferrerButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleRemove = async () => {
    setIsLoading(true);
    try {
      await apiRequest(
        `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.SETTINGS.REMOVE_REFERRER}/${id}`,
        "DELETE",
        { lead_source }
      );
      if (refetch) refetch();
      toast.success("Referrer Successfully Removed");
      setIsModalOpen(false);
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to remove referrer");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => setIsModalOpen(true)}
        className="text-[#E53E3E] block px-4 py-2 text-sm hover:bg-[#FFF5F5] text-left w-full"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="inline-block w-5 h-5 mr-2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
        </svg>
        Remove Referrer
      </button>

      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
            <div className="bg-white p-6 rounded-t-2xl md:rounded-lg w-full md:w-1/3 max-h-[90vh] overflow-y-auto">
              <div className="border-b border-gray-300 mb-6">
                <h2 className="text-xl font-bold mb-2">Remove Referrer</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove this referrer? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-3 bg-white border-[1px] border-black rounded-3xl hover:bg-gray-100"
                >
                  <b>Cancel</b>
                </button>
                <button
                  type="button"
                  onClick={handleRemove}
                  className={`rounded-3xl px-8 py-3 text-white ${
                    isLoading ? "bg-gray-500" : "bg-red-500 hover:bg-red-600"
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? "Removing..." : <b>Remove</b>}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default RemoveReferrerButton;